import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { resourcesApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { FileText, Download, Loader2, BookOpen, Upload, Trash2, File } from "lucide-react";

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(contentType) {
  if (contentType === "application/pdf") return { color: "#EF4444", label: "PDF" };
  if (contentType?.includes("word")) return { color: "#3B82F6", label: "DOCX" };
  if (contentType?.includes("image")) return { color: "#10B981", label: "IMG" };
  return { color: "#94A3B8", label: "TXT" };
}

export default function Resources() {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => { loadResources(); }, []);

  const loadResources = async () => {
    try {
      const res = await resourcesApi.list();
      setResources(res.data);
    } catch {} finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Maximo 10MB"); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await resourcesApi.upload(formData);
      toast.success("Recurso subido correctamente");
      loadResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const handleDownload = async (resource) => {
    setDownloading(resource.id);
    try {
      const res = await resourcesApi.get(resource.id);
      const data = res.data;
      const byteChars = atob(data.data_b64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: data.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || "recurso";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Descargado");
    } catch {
      toast.error("Error al descargar");
    } finally { setDownloading(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este recurso?")) return;
    try {
      await resourcesApi.delete(id);
      toast.success("Recurso eliminado");
      loadResources();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleViewPdf = async (resource) => {
    setDownloading(resource.id);
    try {
      const res = await resourcesApi.get(resource.id);
      const data = res.data;
      const byteChars = atob(data.data_b64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: data.content_type });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      toast.error("Error al abrir");
    } finally { setDownloading(null); }
  };

  return (
    <div data-testid="resources-page" className="min-h-screen" style={{ background: "#0B1622" }}>
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(249,115,22,0.1)" }}>
              <BookOpen className="w-5 h-5" style={{ color: "#F97316" }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>Recursos</h1>
              <p className="text-sm" style={{ color: "#94A3B8" }}>Guias, manuales y documentacion de La Forja</p>
            </div>
          </div>
          {user?.is_admin && (
            <div>
              <input id="resource-upload" type="file" accept=".pdf,.docx,.txt,.png,.jpg,.jpeg" className="hidden" onChange={handleUpload} />
              <Button data-testid="upload-resource-btn" onClick={() => document.getElementById("resource-upload")?.click()}
                disabled={uploading}
                className="rounded-full px-5 text-sm font-medium"
                style={{ background: "linear-gradient(135deg, #F59E0B, #F97316)", color: "#0B1622" }}>
                {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                {uploading ? "Subiendo..." : "Subir recurso"}
              </Button>
            </div>
          )}
        </div>

        {/* Info banner */}
        <div className="rounded-xl border p-5 mb-6" style={{ background: "rgba(249,115,22,0.03)", borderColor: "rgba(249,115,22,0.15)" }}>
          <h3 className="text-sm font-semibold mb-1 flex items-center gap-2" style={{ color: "#F97316" }}>
            <BookOpen className="w-4 h-4" />
            Centro de Recursos
          </h3>
          <p className="text-xs" style={{ color: "#94A3B8" }}>
            Aqui encontraras guias sobre como usar el tono de voz, el enfoque editorial, y como sacar el maximo partido a La Forja de Contenidos.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: "#F97316" }} />
            <span className="text-sm" style={{ color: "#94A3B8" }}>Cargando recursos...</span>
          </div>
        ) : resources.length === 0 ? (
          <div className="rounded-xl border p-12 text-center" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
            <File className="w-12 h-12 mx-auto mb-3" style={{ color: "#475569" }} />
            <h3 className="text-lg font-semibold mb-1" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>Sin recursos todavia</h3>
            <p className="text-sm" style={{ color: "#94A3B8" }}>
              {user?.is_admin ? "Sube PDFs, guias o documentos para tus usuarios." : "El administrador aun no ha subido recursos."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {resources.map((r) => {
              const fi = getFileIcon(r.content_type);
              const isPdf = r.content_type === "application/pdf";
              return (
                <div key={r.id} data-testid={`resource-${r.id}`}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 rounded-xl border transition-all duration-200 hover:border-cyan-500/20"
                  style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${fi.color}15` }}>
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: fi.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: "#E2E8F0", fontFamily: "'Outfit'" }}>{r.title}</div>
                      <div className="flex items-center gap-2 sm:gap-3 mt-0.5 flex-wrap">
                        <span className="text-xs px-2 py-0.5 rounded" style={{ background: `${fi.color}10`, color: fi.color }}>{fi.label}</span>
                        <span className="text-xs" style={{ color: "#64748B" }}>{formatSize(r.size)}</span>
                        <span className="text-xs hidden sm:inline" style={{ color: "#64748B" }}>{new Date(r.created_at).toLocaleDateString("es-ES")}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 pl-13 sm:pl-0">
                    {isPdf && (
                      <Button data-testid={`view-${r.id}`} size="sm" variant="outline"
                        onClick={() => handleViewPdf(r)}
                        disabled={downloading === r.id}
                        className="rounded-full text-xs h-8 px-3"
                        style={{ borderColor: "#1E3A5F", color: "#00D4FF" }}>
                        {downloading === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Ver"}
                      </Button>
                    )}
                    <Button data-testid={`download-${r.id}`} size="sm" variant="outline"
                      onClick={() => handleDownload(r)}
                      disabled={downloading === r.id}
                      className="rounded-full text-xs h-8 px-3"
                      style={{ borderColor: "#1E3A5F", color: "#94A3B8" }}>
                      <Download className="w-3 h-3 mr-1" />Descargar
                    </Button>
                    {user?.is_admin && (
                      <Button data-testid={`delete-resource-${r.id}`} size="sm" variant="outline"
                        onClick={() => handleDelete(r.id)}
                        className="rounded-full text-xs h-8 w-8 p-0"
                        style={{ borderColor: "rgba(239,68,68,0.2)", color: "#EF4444" }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
